import React, { Component } from "react";
import classnames from "classnames";
import { observer } from "mobx-react";

import { isValidTagString } from "@formant/core/common/isValidTagString";
import { ITags } from "@formant/core/model/ITags";
import { ITagSets } from "@formant/core/model/ITagSets";

import { AutocompleteField, IAutocompleteItem } from "./AutocompleteField";
import * as styles from "./EditTags.scss";
import { SelectTags } from "./SelectTags";

export interface IEditTagsProps {
    value: ITags;
    keys: string[];
    tags: ITagSets;
    small?: boolean;
    className?: string;
    description?: string;
    onChange: (value: ITags) => void;
    invalid?: boolean;
    disableInheritedTags?: boolean;
    allowStar?: boolean;
}

interface ITag {
    key: string;
    value: string;
}

@observer
export class EditTags extends Component<IEditTagsProps> {
    public render() {
        const {
            value,
            keys,
            tags,
            small,
            className,
            description,
            onChange,
            invalid,
            disableInheritedTags,
            allowStar,
        } = this.props;

        const items: IAutocompleteItem<ITag>[] = Object.keys(tags).flatMap(
            key => {
                if (!tags[key].includes("*")) {
                    tags[key].push("*");
                }

                return tags[key]
                    .filter(tagValue => !key.startsWith("$.") && !!tagValue)
                    .filter(tagValue => allowStar || tagValue !== "*")
                    .map(tagValue => ({
                        value: { key, value: tagValue },
                        label: `${key}: ${tagValue}`,
                    }));
            },
        );

        const empty = !Object.keys(value).length && !Object.keys(keys).length;

        return (
            <div
                className={classnames(
                    styles["edit-tags"],
                    empty && styles.empty,
                    className,
                )}
            >
                <AutocompleteField
                    label={description || "Add tags"}
                    placeholder={`${
                        description || "Add tags"
                    } (eg. key: value)`}
                    icon="tags"
                    items={items}
                    small={small}
                    className={styles.add}
                    onSelected={this.onSelected}
                    onEnter={this.onEnterTag}
                    invalid={invalid}
                    data-testid="edit-tag-field"
                />
                <SelectTags
                    value={value}
                    keys={keys}
                    tags={tags}
                    className={styles.tags}
                    disableInheritedTags={disableInheritedTags}
                    onChange={onChange}
                />
            </div>
        );
    }

    private onSelected = (tag: ITag) => {
        const { value, onChange } = this.props;
        onChange({
            ...value,
            [tag.key]: tag.value,
        });
    };

    private onEnterTag = (keyValue: string) => {
        const { allowStar } = this.props;
        const [key, value] = keyValue.split(":").map(_ => _.trim());
        if (
            key &&
            value &&
            isValidTagString(key) &&
            isValidTagString(value, { allowStar })
        ) {
            this.onSelected({ key, value });
            return true;
        }
        return false;
    };
}
